// How the dispatch-session poller decides the BE is "done" creating the bulk
// add-to-job / move executions, so it can enqueue the underway row.
//
// The BE stamps `dispatch_expected_total` from the RAW selected count, but the
// async adapter skips candidates already in the job (and invalid / consent-
// blocked ones), so the real execution count is often LOWER — meaning
// `dispatch_ready` (count >= expected_total) can never flip. Relying on it alone
// leaves the row stuck until the 60s deadline (or never, if all were skipped).
//
// So we settle on EITHER signal: `dispatch_ready`, OR the execution count has
// stopped growing across a few consecutive polls (the worker has finished — a
// positive count that no longer changes means "as many as we're getting").

// Consecutive polls the count must stay unchanged (and > 0) before we treat the
// worker as done. At the 1.5s poll interval this settles ~3s after the last
// execution appears — long enough to ride out a batch gap, short enough to feel
// immediate.
export const SETTLE_STABLE_POLLS = 2;

export type SettleState = {
  // Execution count seen on the previous poll (null before the first).
  lastCount: number | null;
  // How many consecutive polls the count has been unchanged (and > 0).
  stablePolls: number;
};

export const initialSettleState: SettleState = {
  lastCount: null,
  stablePolls: 0,
};

type SettleResult = { executions: unknown[]; dispatchReady: boolean };

// Given the running state and the latest poll result, return the next state and
// whether the session has settled (time to enqueue). Pure — the poller keeps
// `state` in a ref and enqueues when `settle` is true.
export const nextSettleState = (
  state: SettleState,
  result: SettleResult | undefined
): { state: SettleState; settle: boolean } => {
  if (!result) return { state, settle: false };

  // BE says every expected execution exists — settle immediately (happy path).
  if (result.dispatchReady) return { state, settle: true };

  const count = result.executions.length;

  // A count still at 0 means nothing has been created yet — keep waiting; never
  // settle on an empty result (that path is the deadline's job, and yields no
  // row when the stage genuinely has no automation).
  if (count > 0 && count === state.lastCount) {
    const stablePolls = state.stablePolls + 1;
    return {
      state: { lastCount: count, stablePolls },
      settle: stablePolls >= SETTLE_STABLE_POLLS,
    };
  }

  // First non-zero count, or the count changed — reset the stability streak.
  return { state: { lastCount: count, stablePolls: 0 }, settle: false };
};
